// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

// Enhanced production support with strict error handling and recovery mechanisms
// Production mode includes:
// - Strict service worker registration with timeout protection
// - Enhanced error handling with automatic recovery
// - Detailed logging for production debugging
// - Automatic page reload on critical failures
// - Simplified environment-based logic (development vs production only)

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  const isProduction = process.env.NODE_ENV === 'production';

  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      console.warn('⚠️ Service Worker origin mismatch. PUBLIC_URL origin differs from page origin.');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      // Simple environment-based registration without localhost complexity
      if (isProduction) {
        console.log('🏭 Production mode: Starting strict service worker registration...');
        registerValidSWProduction(swUrl, config);
      } else {
        console.log('🔧 Development mode: Starting standard service worker registration...');
        registerValidSW(swUrl, config);
      }
    });
  } else {
    if (isProduction) {
      console.error('❌ Production: Service Worker not supported in this browser');
    } else {
      console.log('⚠️ Development mode: Service Worker not supported in this browser');
    }
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

// Production-specific service worker registration with enhanced error handling
function registerValidSWProduction(swUrl: string, config?: Config) {
  console.log('🏭 Production: Starting strict service worker registration...');
  
  // Add timeout for production registration
  const registrationPromise = navigator.serviceWorker.register(swUrl);
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Service Worker registration timeout after 15 seconds')), 15000);
  });

  Promise.race([registrationPromise, timeoutPromise])
    .then((registration) => {
      console.log('✅ Production: Service Worker registered successfully');
      
      // Enhanced update handling for production
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        console.log('🔄 Production: Service Worker update found, installing...');
        
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available
              console.log('✅ Production: New content is available and will be used when all tabs are closed.');
              
              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // First time installation
              console.log('✅ Production: Service Worker installed successfully. Content is cached for offline use.');
              
              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          } else if (installingWorker.state === 'activating') {
            console.log('🔄 Production: Service Worker activating...');
          } else if (installingWorker.state === 'activated') {
            console.log('✅ Production: Service Worker activated successfully');
          }
        };
      };

      // Enhanced error handling for production
      registration.addEventListener('error', (event) => {
        console.error('❌ Production: Service Worker error:', event);
      });

      // Monitor service worker lifecycle
      registration.addEventListener('updatefound', () => {
        console.log('🔄 Production: Service Worker update found');
      });

      registration.addEventListener('statechange', () => {
        console.log(`🔄 Production: Service Worker state changed to: ${registration.active?.state || 'unknown'}`);
      });
    })
    .catch((error) => {
      console.error('❌ Production: Service Worker registration failed:', error);
      
      // In production, try to recover by reloading the page
      if (process.env.NODE_ENV === 'production') {
        console.log('🔄 Production: Attempting to recover by reloading page...');
        setTimeout(() => {
          //window.location.reload();
        }, 3000);
      }
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    const isProduction = process.env.NODE_ENV === 'production';
    
    if (isProduction) {
      console.log('🏭 Production: Unregistering service worker...');
    }
    
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister().then((unregistered) => {
          if (unregistered) {
            if (isProduction) {
              console.log('✅ Production: Service Worker unregistered successfully');
            } else {
              console.log('✅ Service Worker unregistered successfully');
            }
          } else {
            if (isProduction) {
              console.warn('⚠️ Production: Service Worker was not registered');
            } else {
              console.warn('⚠️ Service Worker was not registered');
            }
          }
        });
      })
      .catch((error) => {
        if (isProduction) {
          console.error('❌ Production: Failed to unregister service worker:', error.message);
        } else {
          console.error('❌ Failed to unregister service worker:', error.message);
        }
      });
  } else {
    if (process.env.NODE_ENV === 'production') {
      console.log('🏭 Production: Service Worker not supported, nothing to unregister');
    }
  }
}
